import { useSearchParams } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useAssetOutlet } from "~/pages/inventory/asset";
import { KeyValue } from "~/lib/types";
import { AssetAnnotations } from "~/components/asset-annotations";
import { AssetPlatformOverview } from "~/components/asset-platform-overview";
import { LoadingFailedPage } from "~/components/loading";
import {
  LoadAssetDocument,
  useSetAssetAnnotationsMutation,
} from "~/operations";
import { ConfigurationAssetOverview } from "./asset-configuration-asset-overview";
import { Asset } from "~/pages/inventory/asset/types";
import { useCanSetAssetAnnotations } from "~/lib/iam/actions/mondoo/policy/AssetStore/SetAssetAnnotations";

export type AssetOverviewGroup = Asset["overviewGroups"][0];
export type AssetOverviewItem = AssetOverviewGroup["items"][0];

type Props = {};

export function AssetConfigurationSection({}: Props) {
  const { space, asset, assetIntegration, assetServiceAccount, isCicd } =
    useAssetOutlet();
  const [searchParams, _setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const assetMrn = isCicd ? searchParams.get("jobId") : asset.mrn;

  if (!assetMrn || !asset) {
    return <LoadingFailedPage what="asset" />;
  }

  const hasSetAssetAnnotationsPermission = useCanSetAssetAnnotations(space);

  const [setAssetAnnotations] = useSetAssetAnnotationsMutation({
    refetchQueries: [LoadAssetDocument],
  });

  const onAnnotationsSubmit = async (annotations: KeyValue[]) => {
    try {
      await setAssetAnnotations({
        variables: {
          input: { spaceMrn: space.mrn, mrn: assetMrn, annotations },
        },
      });
    } catch (error) {
      enqueueSnackbar("Failed to set asset annotations", { variant: "error" });
    }
  };

  return (
    <Grid container pt={3}>
      <Grid
        size={12}
        sx={{
          p: 3,
          pb: 1,
          color: "text.secondary",
          backgroundColor: "background.paper",
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <Typography
          variant="h5"
          component="h3"
          fontWeight={700}
          sx={{ color: "text.primary", lineHeight: "24px" }}
        >
          CONFIGURATION
        </Typography>
      </Grid>

      <Grid
        size={12}
        sx={{
          px: 3,
          pb: 3,
          backgroundColor: "background.paper",
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          boxShadow: (theme) => theme.shadows[1],
        }}
      >
        <Grid container columnSpacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <AssetPlatformOverview asset={asset} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ConfigurationAssetOverview
              asset={asset}
              integration={assetIntegration}
              serviceAccount={assetServiceAccount}
              space={space}
            />
            <AssetAnnotations
              asset={asset}
              readOnly={!hasSetAssetAnnotationsPermission}
              onSubmit={onAnnotationsSubmit}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export const configurationTableSx = {
  "th, td": {
    pt: 0,
    pb: 1,
    borderBottom: "none",
    wordBreak: "break-word",
    lineHeight: "24px",
    verticalAlign: "top",
  },
  "tr:first-of-type td": {
    pt: 1,
  },
  "& .key": {
    color: "text.secondary",
  },
  "& .width-50": {
    width: "50%",
  },
};
